import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors, TailwindColorValue } from '@repo/tailwind-config/tailwind.vars'
import { FileTypeEnum } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import { DisplayImage } from '@repo/ui/file-input'
import {
  HealthFormIcon,
  ListApprovedIcon,
  PassportIcon,
  TravelTwoIcon,
  UserTwoIcon
} from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'

import { API, constructApiUrl } from '../../../api/apis.config'
import InformationToolTip from '../../../components/elements/information-tooltip'
import { EnrollmentProfileModel } from '../../../components/pages/enrollment-profile/enrollment-profile-model'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { EnrollmentInformationStatus } from '../../../lib/types/enums/enrollment/enrollment-information-status.enum'
import { AccomodationCategoryEnum } from '../../../lib/types/enums/product/accomodation-category.enum'
import { Enrollment } from '../../../lib/types/models/enrollment/enrollment'
import { EnrollmentPartial } from '../../../lib/types/models/enrollment/enrollment-light'

const getPickupColorStatus = (
  enrollment: Pick<Enrollment, 'childPickupSubmitted'> & {
    mainProposition?: {
      mainTrip?: Pick<
        NonNullable<NonNullable<Enrollment['mainProposition']>['mainTrip']>,
        'hasTransport'
      >
    }
  }
): TailwindColorValue => {
  // If trip has transport, pickup is not applicable - return grey
  const hasTransport = enrollment.mainProposition?.mainTrip?.hasTransport
  if (hasTransport) return 'grey'

  // If pickup is submitted, return green
  if (enrollment.childPickupSubmitted) return 'green'

  // If not submitted, return red
  // (Only editable within 3 days of departure, but we show red to indicate it needs attention)
  return 'red'
}

type Props = {
  enrollment: Pick<
    EnrollmentPartial,
    | 'id'
    | 'childInfoStatus'
    | 'childIdentityPapersSubmitted'
    | 'childHealthPapersSubmitted'
    | 'hasTest'
    | 'hasSubmittedTest'
    | 'childPickupSubmitted'
  > & {
    mainProposition?: {
      mainTrip?: Pick<
        NonNullable<NonNullable<Enrollment['mainProposition']>['mainTrip']>,
        'hasTransport'
      >
      journey?: {
        date: { product: { accomodationCategory: AccomodationCategoryEnum } }
      }
    }
  }
  onEnrollmentUpdate?: () => void
}

const EnrollmentIconsStatus = ({ enrollment, onEnrollmentUpdate }: Props) => {
  const { downloadDocuments } = useDownloadDocuments()
  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'view-enrollment-profile-pdf' | 'view-medical-pdf',
    string
  >(['view-enrollment-profile-pdf', 'view-medical-pdf'])

  async function handleMedicalClick(enrollmentId: number) {
    const { fileLink } = await downloadDocuments(
      {
        endpoint: constructApiUrl(API.enrollments.exportEnrollmentHealthPdf.url, {
          id: enrollmentId
        })
      },
      { automaticallyDownload: false }
    )

    if (fileLink) toggleModal('view-medical-pdf', fileLink)
  }

  async function handleProfilePdfClick(enrollmentId: number) {
    const { fileLink } = await downloadDocuments(
      {
        endpoint: constructApiUrl(API.enrollments.exportEnrollmentProfilePdf.url, {
          id: enrollmentId
        })
      },
      { automaticallyDownload: false }
    )

    if (fileLink) toggleModal('view-enrollment-profile-pdf', fileLink)
  }

  const profileColor: TailwindColorValue =
    enrollment.childInfoStatus === EnrollmentInformationStatus.COMPLETED
      ? 'green'
      : enrollment.childInfoStatus === EnrollmentInformationStatus.TO_BE_COMPLETED
        ? 'orange'
        : 'red'
  const identityColor: TailwindColorValue = enrollment.childIdentityPapersSubmitted
    ? 'green'
    : 'red'

  const testColor: TailwindColorValue = !enrollment.hasTest
    ? 'grey'
    : enrollment.hasSubmittedTest
      ? 'green'
      : 'red'
  const medicalColor: TailwindColorValue = enrollment.childHealthPapersSubmitted
    ? 'green'
    : enrollment.mainProposition?.journey?.date.product.accomodationCategory ===
        AccomodationCategoryEnum.COLLEGE_CAMPUS_RESIDENCE
      ? 'red'
      : 'grey'
  const travelColor: TailwindColorValue = getPickupColorStatus(enrollment)

  return (
    // 3 + 2 icons instead of a single row, to keep the column narrow
    <div className="grid grid-cols-3 gap-1 w-max">
      <InformationToolTip
        size="small"
        icon={
          <Badge
            color={tailwindColors[profileColor].light.DEFAULT}
            text={<UserTwoIcon color={tailwindColors[profileColor].DEFAULT} />}
            onClick={async () => {
              if (enrollment.childInfoStatus === EnrollmentInformationStatus.TO_BE_COMPLETED)
                await handleProfilePdfClick(enrollment.id)
            }}
          />
        }
      >
        <Typography variant={'label-medium'} color={tailwindColors.white.DEFAULT}>
          Profil
        </Typography>
      </InformationToolTip>
      <InformationToolTip
        size="small"
        icon={
          <Badge
            color={tailwindColors[identityColor].light.DEFAULT}
            text={<PassportIcon color={tailwindColors[identityColor].DEFAULT} />}
          />
        }
      >
        <Typography variant={'label-medium'} color={tailwindColors.white.DEFAULT}>
          Pièces d’identité
        </Typography>
      </InformationToolTip>

      <InformationToolTip
        size="small"
        icon={
          <Badge
            color={tailwindColors[testColor].light.DEFAULT}
            text={<ListApprovedIcon color={tailwindColors[testColor].DEFAULT} />}
          />
        }
      >
        <Typography variant={'label-medium'} color={tailwindColors.white.DEFAULT}>
          Test de niveau
        </Typography>
      </InformationToolTip>

      <InformationToolTip
        size="small"
        icon={
          <Badge
            color={tailwindColors[medicalColor].light.DEFAULT}
            text={<HealthFormIcon color={tailwindColors[medicalColor].DEFAULT} />}
            onClick={async () => {
              if (enrollment.childHealthPapersSubmitted) await handleMedicalClick(enrollment.id)
            }}
          />
        }
      >
        <Typography variant={'label-medium'} color={tailwindColors.white.DEFAULT}>
          Fiche de liaison médicale
        </Typography>
      </InformationToolTip>

      <InformationToolTip
        size="small"
        icon={
          <Badge
            color={tailwindColors[travelColor as 'grey' | 'red' | 'green'].light.DEFAULT}
            text={
              <TravelTwoIcon
                color={tailwindColors[travelColor as 'grey' | 'red' | 'green'].DEFAULT}
              />
            }
          />
        }
      >
        <Typography variant={'label-medium'} color={tailwindColors.white.DEFAULT}>
          Informations sur le parcours individuel
        </Typography>
      </InformationToolTip>

      {selectedData && (
        <>
          <EnrollmentProfileModel
            data={enrollment}
            onEnrollmentUpdate={onEnrollmentUpdate}
            src={selectedData}
            isOpen={modalState['view-enrollment-profile-pdf']}
            onCancel={() => closeModel('view-enrollment-profile-pdf')}
          />
          <Modal
            isOpen={modalState['view-medical-pdf']}
            title="Fiche de liaison médicale"
            icon={HealthFormIcon}
            size="medium"
            hideActions
            onCancel={() => closeModel('view-medical-pdf')}
          >
            <div className={cn(MODAL_CONTENT_CLASSNAME)}>
              <DisplayImage
                allowAll
                type={FileTypeEnum.PDF}
                src={selectedData}
                className="h-[60dvh]"
              />
            </div>
          </Modal>
        </>
      )}
    </div>
  )
}

export default EnrollmentIconsStatus
